import type { ChoroplethBin, ChoroplethSemanticFamily, ValueFormatter } from './choroplethDesignSystem';
import {
  buildPoliticalDivergingBins,
  buildQuantileBins,
  buildZeroAwareQuantileBins,
  classifyPoliticalValue,
  classifySequentialValue,
  classifySequentialValueWithZeroClass,
  CHOROPLETH_NO_DATA_FILL,
} from './choroplethDesignSystem';
import { buildTotalChargerBins, colorForChargerValue } from './mapChargerBinning';

/**
 * Plug-in LDV count at tract level from ev_by_tract_2023.
 * Prefer native aggregated fleet count columns when present; otherwise derive from rate × population
 * (same rollup as populate_ev_per_1000_from_tracts.sql when only rate is stored).
 */
/** Share of households with no vehicle available (0–100 scale for map display). */
export function zeroVehicleHouseholdPctFromRow(t: Record<string, unknown>): number | null {
  const pctRaw = t.pct_zero_vehicle_households;
  if (pctRaw == null || !Number.isFinite(Number(pctRaw))) return null;
  const pct = Number(pctRaw);
  return pct <= 1 && pct >= 0 ? pct * 100 : pct;
}

export function tractTotalEvsFromRow(t: Record<string, unknown>): number | null {
  const native =
    t?.total_plugin_evs ?? t?.total_evs ?? t?.plugin_ev_count ?? t?.ev_count;
  if (native != null) {
    const n = Number(native);
    if (Number.isFinite(n)) return n;
  }
  const rate = t?.evs_per_100_people;
  const pop = t?.total_population;
  if (rate == null || pop == null) return null;
  const r = Number(rate);
  const p = Number(pop);
  if (!Number.isFinite(r) || !Number.isFinite(p)) return null;
  return (r / 100) * p;
}

export type MapVariableId =
  | 'income'
  | 'ev_adoption'
  | 'total_evs'
  | 'total_chargers'
  | 'vehicles_per_household'
  | 'population_density'
  | 'political_lean'
  | 'carbon_footprint'
  | 'commute_time'
  | 'zero_vehicle_households'
  | 'owner_occupied_housing'
  | 'educational_attainment';

export type VariableSemantics = {
  family: ChoroplethSemanticFamily;
  classCount: number;
};

export const MAP_VARIABLE_SEMANTICS: Record<MapVariableId, VariableSemantics> = {
  income: { family: 'neutral', classCount: 7 },
  population_density: { family: 'neutral', classCount: 7 },
  educational_attainment: { family: 'neutral', classCount: 7 },
  owner_occupied_housing: { family: 'neutral', classCount: 7 },
  vehicles_per_household: { family: 'neutral', classCount: 7 },
  zero_vehicle_households: { family: 'neutral', classCount: 7 },
  total_chargers: { family: 'neutral', classCount: 7 },
  ev_adoption: { family: 'benefit', classCount: 7 },
  total_evs: { family: 'benefit', classCount: 7 },
  commute_time: { family: 'burden', classCount: 7 },
  carbon_footprint: { family: 'burden', classCount: 7 },
  political_lean: { family: 'political', classCount: 7 },
};

export type MapClassificationResult = {
  bins: ChoroplethBin[];
  /** Legend subtitle for breakpoint source */
  breakContext: string;
};

export function extractNumericValuesForVariable(
  data: any[],
  variableId: MapVariableId,
  getField: (id: MapVariableId) => string
): number[] {
  const field = getField(variableId);
  const values: number[] = [];

  for (const t of data) {
    let v: number | null = null;
    if (variableId === 'carbon_footprint') {
      const totalEmissions =
        (t.transportation_total_emissions || 0) +
        (t.housing_total_emissions || 0) +
        (t.food_total_emissions || 0) +
        (t.goods_total_emissions || 0) +
        (t.services_total_emissions || 0);
      v = totalEmissions / 1_000_000;
    } else if (variableId === 'zero_vehicle_households') {
      v = zeroVehicleHouseholdPctFromRow(t);
      if (v == null) continue;
    } else if (variableId === 'total_evs') {
      v = tractTotalEvsFromRow(t);
    } else {
      const raw = t?.[field];
      if (raw != null && !Number.isNaN(Number(raw))) {
        v = typeof raw === 'string' ? parseFloat(String(raw).replace(/[,]/g, '')) : Number(raw);
      }
    }
    if (v == null || !Number.isFinite(v)) continue;
    if (variableId === 'political_lean') v *= 100;
    if (variableId === 'owner_occupied_housing' || variableId === 'educational_attainment') v *= 100;
    values.push(v);
  }
  return values;
}

function formatDerivedHouseholdCount(v: number): string {
  const abs = Math.abs(v);
  if (abs < 1) return v.toFixed(3);
  if (abs < 10) return v.toFixed(2);
  if (abs < 100) return v.toFixed(1);
  return Math.round(v).toLocaleString();
}

function formatterForVariable(variableId: MapVariableId): ValueFormatter {
  if (variableId === 'owner_occupied_housing' || variableId === 'educational_attainment') {
    return (v) => `${v.toFixed(1)}%`;
  }
  if (variableId === 'commute_time') return (v) => `${v.toFixed(1)}`;
  if (variableId === 'carbon_footprint') return (v) => v.toFixed(1);
  if (variableId === 'income') return (v) => `$${Math.round(v).toLocaleString()}`;
  if (variableId === 'vehicles_per_household') return (v) => v.toFixed(2);
  if (variableId === 'ev_adoption') return (v) => v.toFixed(2);
  if (variableId === 'total_evs') return formatDerivedHouseholdCount;
  if (variableId === 'zero_vehicle_households') return (v) => `${v.toFixed(1)}%`;
  return defaultFormatter;
}

const defaultFormatter: ValueFormatter = (v) =>
  Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 2 });

export function buildClassificationForVariable(
  values: number[],
  variableId: MapVariableId,
  breakContext: string
): MapClassificationResult | null {
  if (values.length === 0 && variableId !== 'total_chargers') return null;

  const { family, classCount } = MAP_VARIABLE_SEMANTICS[variableId];
  const fmt = formatterForVariable(variableId);

  if (variableId === 'total_chargers') {
    return { bins: buildTotalChargerBins(), breakContext };
  }

  if (variableId === 'political_lean') {
    const maxAbs = values.reduce((m, v) => Math.max(m, Math.abs(v)), 0);
    return {
      bins: buildPoliticalDivergingBins(maxAbs, classCount),
      breakContext,
    };
  }

  if (variableId === 'zero_vehicle_households') {
    const bins = buildZeroAwareQuantileBins(values, classCount, family, fmt);
    return { bins, breakContext };
  }

  return {
    bins: buildQuantileBins(values, classCount, family, fmt),
    breakContext,
  };
}

export function colorValueForVariable(variableId: MapVariableId, value: number, bins: ChoroplethBin[]): string {
  if (!Number.isFinite(value)) return CHOROPLETH_NO_DATA_FILL;
  if (variableId === 'political_lean') return classifyPoliticalValue(value, bins);
  if (variableId === 'total_chargers') return colorForChargerValue(value, bins);
  if (variableId === 'zero_vehicle_households' && bins[0]?.upper === 0 && bins[0]?.lower === 0) {
    return classifySequentialValueWithZeroClass(value, bins);
  }
  return classifySequentialValue(value, bins);
}
